/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../events';
import { Logger } from '../logger';
import { reportRuleFailure } from '../rules/debug';
import { createFactLayer } from '../rules/facts/resolver';
import { FactScope, internConditions, evaluateCompiled, shareKeyFor } from '../rules/ruleset';
import { AdIdType, CampaignType } from '../types/index';
import { clone } from '../util';
import { ActivityManager } from '../activity/manager';
import { Ad } from './ad';
import { BrandSafety, BrandSafetyEvents, BrandSafetyManager } from './advertiser';

import type { CompiledRuleSet } from '../rules/ruleset';
import type { FailedCondition, Facts } from '../rules';
import type { AdAssignedKeyValues, CampaignData, KeyValue } from '../types/index';
import type { HierarchyNode } from '../activity/manager';

const logger = Logger.getInstance();

/**
 * Campaign types that observe ad delivery rather than directly contributing
 * to it - learning campaigns and passive monitoring. They share event-emission
 * semantics (`t=1`) and are excluded from key-value assignment.
 */
export const OBSERVATIONAL_CAMPAIGN_TYPES: readonly CampaignType[] = [
  CampaignType.LEARNING_DIRECT,
  CampaignType.LEARNING_PROGRAMMATIC,
  CampaignType.MONITORING,
];

/**
 * Campaign types whose ads are reported as "untagged monitored" deliveries -
 * direct campaigns whose line/creative IDs are tracked at the ad-server level
 * but do not deliver via Nodals' assignment.
 */
export const UNTAGGED_MONITORED_CAMPAIGN_TYPES: readonly CampaignType[] = [
  CampaignType.DIRECT,
  CampaignType.SCORING_DIRECT,
  CampaignType.MONITORING,
];

class Campaign extends EventDispatcher<BrandSafetyEvents> {
  private ads: Ad[] = [];
  private data: CampaignData;
  private ruleSet: CompiledRuleSet;
  private parentFacts: () => Facts;
  private hasDbgAd: boolean;
  // campaign.* activity facts; layered over the placement facts
  private campaignFacts: Facts = {};
  private campaignFactsDirty: boolean = true;
  private evalFacts: Facts | null = null;
  private evalParent: Facts | null = null;
  private gateResult: boolean | null = null;
  private brandSafety: BrandSafety | null = null;
  private activeBrandViolation: boolean = false;
  // Outermost performance gate: brand safety does no DOM work while the placement is
  // off-screen. Supplied by the placement via enableBrandSafetyMonitoring().
  private placementVisible: () => boolean = () => true;
  private brandSafetyManager: BrandSafetyManager = BrandSafetyManager.getInstance();
  private activityRecordedListener: ((event: any) => void) | null = null;
  private previouslySetKeys: Set<string> = new Set();

  constructor(campaignData: CampaignData, parentFacts: () => Facts) {
    super();
    this.data = clone(campaignData);
    this.parentFacts = parentFacts;
    this.ruleSet = internConditions(this.data.rules?.conditions ?? {});
    this.hasDbgAd = this.data.ads.some((adData) => adData.dbg === true);
    this.ads = this.data.ads.map((adData) => new Ad(adData));
    this.setupActivityListener();
  }

  private setupActivityListener(): void {
    const activityManager = ActivityManager.getInstance();
    this.activityRecordedListener = (event: any) => {
      if (this.isAffectedByActivity(event.data.entityId)) {
        // Campaign counts aggregate child-ad activity, so any hit dirties the
        // campaign layer; the rebuilt layer identity re-evaluates every ad
        this.campaignFactsDirty = true;
      }
    };
    activityManager.on('ActivityRecorded', this.activityRecordedListener);
  }

  private isAffectedByActivity(entityId: string): boolean {
    // Check if the activity affects this campaign or any of its ads
    return entityId === this.id || this.ads.some((ad) => ad.deliveryId === entityId);
  }

  private refreshCampaignActivityFacts(): void {
    const hierarchy: HierarchyNode = {
      id: this.id,
      children: this.ads.map((ad) => ({ id: ad.deliveryId, children: [] })),
    };

    const activityManager = ActivityManager.getInstance();

    // Historical facts (30-day)
    this.campaignFacts['campaign.imps'] = activityManager.getImpsForEntity(this.id, hierarchy);
    this.campaignFacts['campaign.views'] = activityManager.getViewsForEntity(this.id, hierarchy);
    this.campaignFacts['campaign.ints'] = activityManager.getInteractionsForEntity(
      this.id,
      hierarchy,
    );

    // Page view facts (current page only)
    this.campaignFacts['campaign.page.imps'] = activityManager.getImpsForEntity(
      this.id,
      hierarchy,
      true,
    ).length;
    this.campaignFacts['campaign.page.views'] = activityManager.getViewsForEntity(
      this.id,
      hierarchy,
      true,
    ).length;
    this.campaignFacts['campaign.page.ints'] = activityManager.getInteractionsForEntity(
      this.id,
      hierarchy,
      true,
    ).length;
    this.campaignFactsDirty = false;
  }

  /**
   * The campaign-scope fact layer, rebuilt when the placement layer's
   * identity changes (a new epoch or a placement fact change) or campaign
   * activity was recorded. A rebuild yields a new object identity, which is
   * what invalidates every child ad's memoised eligibility.
   */
  private getEvaluationFacts(): Facts {
    const parent = this.parentFacts();
    if (!this.evalFacts || this.evalParent !== parent || this.campaignFactsDirty) {
      if (this.campaignFactsDirty) {
        this.refreshCampaignActivityFacts();
      }
      this.evalFacts = createFactLayer(parent, this.campaignFacts);
      this.evalParent = parent;
      this.gateResult = null;
    }
    return this.evalFacts;
  }

  /**
   * Evaluates the campaign-level conditions once per fact-layer rebuild.
   * A failing gate makes every ad ineligible without evaluating any of them.
   */
  private passesGate(): boolean {
    if (this.ruleSet.isEmpty) {
      return true;
    }
    const parentFacts = this.parentFacts();
    // Campaigns that would ask this set the same question reuse one
    // evaluation; a campaign with a dbg ad always evaluates so rdbg fires.
    const shareKey = this.hasDbgAd
      ? null
      : shareKeyFor(this.ruleSet, FactScope.CAMPAIGN, parentFacts);
    if (shareKey !== null && this.ruleSet.shareKey === shareKey) {
      return this.ruleSet.sharedResult!;
    }

    const facts = this.getEvaluationFacts();
    if (this.gateResult === null) {
      this.gateResult = evaluateCompiled(this.ruleSet, facts);
      if (!this.gateResult) {
        this.reportGateFailure(facts);
      }
    }
    if (shareKey !== null) {
      this.ruleSet.shareKey = shareKey;
      this.ruleSet.sharedResult = this.gateResult;
    }
    return this.gateResult;
  }

  // One rdbg per distinct failing gate signature, attributed to the first dbg ad
  private reportGateFailure(facts: Facts): void {
    const dbgAdData = this.data.ads.find((adData) => adData.dbg === true);
    if (!dbgAdData) {
      return;
    }
    const collector: FailedCondition[] = [];
    evaluateCompiled(this.ruleSet, facts, collector);
    if (collector.length > 0) {
      reportRuleFailure(dbgAdData.id, facts, collector, 'c');
    }
  }

  public get adDeliveryIds(): string[] {
    return this.ads.map((ad) => ad.deliveryId);
  }

  public get id(): string {
    return this.data.id;
  }

  public get type(): CampaignType {
    return this.data.type;
  }

  public get deals(): string[] {
    return this.data?.deals || [];
  }

  public get pageIsBrandCompliant(): boolean {
    return !this.isBrandUnsafe();
  }

  /**
   * Lazy brand-safety check at the pull point. Asks the (shared) BrandSafety monitor
   * whether a banned word is currently visible, scanning the DOM only if it changed and
   * only while this placement is visible. Mirrors the result into `activeBrandViolation`
   * so synchronous readers stay consistent within a pull.
   */
  private isBrandUnsafe(): boolean {
    if (!this.brandSafety) {
      return this.activeBrandViolation;
    }
    this.activeBrandViolation = this.brandSafety.hasVisibleViolation(this.placementVisible);
    return this.activeBrandViolation;
  }

  public get linkedExternalLineIds(): string[] {
    const linkedLineIds = Array.from(
      new Set(
        this.ads
          .map((ad) => ad.linkedLineId)
          .filter((id): id is string => id !== null && id !== undefined),
      ),
    );
    return linkedLineIds;
  }

  public get linkedExternalCreativeIds(): string[] {
    const linkedCreativeIds = Array.from(
      new Set(
        this.ads
          .map((ad) => ad.linkedCreativeId)
          .filter((id): id is string => id !== null && id !== undefined),
      ),
    );
    return linkedCreativeIds;
  }

  /**
   * Find matching ad delivery ID by line ID and/or creative ID.
   * Matching is based on what the Ad instance has configured:
   * - If Ad has only linkedCreativeId: match if creativeId matches (ignore lineId)
   * - If Ad has only linkedLineId: match if lineId matches (ignore creativeId)
   * - If Ad has both: match only if BOTH lineId and creativeId match
   *
   * When `requireKeyValuesSet` is true, only ads whose key-values were set on
   * the ad call for the current opportunity (populated by the selection
   * endpoint response) are considered. Used to disambiguate the case where
   * two sibling campaigns share the same creative ID and only one had its
   * rule fire.
   *
   * @param lineId Optional line ID to match
   * @param creativeId Optional creative ID to match
   * @param requireKeyValuesSet When true, restrict candidates to ads with non-empty keyValues
   * @returns The delivery ID of the matching ad, or undefined if no match
   */
  public getMatchingAdDeliveryId(
    lineId?: string,
    creativeId?: string,
    requireKeyValuesSet = false,
  ): string | undefined {
    const matchingAd = this.ads.find((ad) => {
      if (requireKeyValuesSet && !ad.hasKeyValues()) {
        return false;
      }

      if (lineId && creativeId) {
        return ad.linkedLineId === lineId && ad.linkedCreativeId === creativeId;
      }

      if (lineId && !creativeId) {
        return ad.linkedLineId === lineId;
      }

      if (!lineId && creativeId) {
        return ad.linkedCreativeId === creativeId;
      }

      return false;
    });
    if (matchingAd) {
      return matchingAd.deliveryId;
    }
  }

  public getEligibleAdIds(): string[] {
    if (this.isBrandUnsafe() || !this.passesGate()) {
      return [];
    }
    const facts = this.getEvaluationFacts();
    const filteredAds = this.ads.filter((ad) => ad.isEligible(facts));
    return filteredAds.map((ad) => ad.deliveryId);
  }

  public hasEligibleAds(): boolean {
    if (!this.passesGate()) {
      return false;
    }
    const facts = this.getEvaluationFacts();
    return this.ads.some((ad) => ad.isEligible(facts));
  }

  public get keyValuesByAd(): AdAssignedKeyValues[] {
    return this.ads.map((ad) => ({
      id: ad.deliveryId,
      kvs: ad.keyValues,
    }));
  }

  public get keyValues(): KeyValue[] {
    const kvs: KeyValue[] = [];

    if (!this.isBrandUnsafe()) {
      this.ads.forEach((ad) => {
        ad.keyValues.forEach((kv) => {
          const existingKv = kvs.find((existingKv) => existingKv.key === kv.key);
          if (!existingKv) {
            kvs.push({ key: kv.key, value: kv.value });
          } else {
            const existingValues = existingKv.value.split(',');
            if (!existingValues.includes(kv.value)) {
              existingKv.value += `,${kv.value}`;
            }
          }
        });
      });
    }

    const currentKeys = new Set(kvs.map((kv) => kv.key));

    // Find keys that were previously set but are no longer present
    // This handles both brand violations (all keys cleared) and selective clearing
    const keysToClears = Array.from(this.previouslySetKeys).filter((key) => !currentKeys.has(key));

    // Clear stale keys by setting them to empty string
    // This is required because of the Prebid issue where if no key-values are set
    // it will use the last known key-values for the ad unit.
    keysToClears.forEach((key) => {
      kvs.push({ key, value: '' });
    });

    // Update tracking: store all keys we're currently managing (both current and cleared)
    this.previouslySetKeys = new Set([...currentKeys, ...keysToClears]);

    return kvs;
  }

  public set adKeyValues(adAssignedKvs: AdAssignedKeyValues[]) {
    if (OBSERVATIONAL_CAMPAIGN_TYPES.includes(this.type)) {
      // Never set key values for learning or monitoring campaigns
      return;
    }
    adAssignedKvs.forEach((adAssignedKv) => {
      const ad = this.ads.find((a) => a.deliveryId === adAssignedKv.id);
      if (ad) {
        ad.keyValues = adAssignedKv.kvs ?? [];
        ad.selectionLabel = adAssignedKv.l;
      }
    });
  }

  public clearAdKeyValues(): void {
    this.ads.forEach((ad) => {
      ad.keyValues = [];
    });
  }

  public getAdKeyValues(id: string, type: AdIdType): KeyValue[] {
    logger.info(`Fetching key values for reporting ad with ID: ${id} of type: ${type}`);
    const matchingAd = this.ads.find((ad) =>
      type === AdIdType.EXTERNAL_ID ? ad.linkedLineId === id : ad.deliveryId === id,
    );
    return matchingAd ? matchingAd.keyValues : [];
  }

  public getAdSelectionLabel(id: string, type: AdIdType): string | undefined {
    const matchingAd = this.ads.find((ad) => {
      // Only consider ads that have key-values set (i.e., the selected ad)
      // This handles cases where multiple ads share the same external ID
      if (!ad.hasKeyValues()) {
        return false;
      }
      return type === AdIdType.EXTERNAL_ID ? ad.linkedLineId === id : ad.deliveryId === id;
    });
    return matchingAd?.selectionLabel;
  }

  /**
   * Check if this campaign is associated with the given external ID(s).
   * Matching is based on what the Ad instance has configured:
   * - If Ad has only linkedCreativeId: match if creativeId matches (ignore lineId)
   * - If Ad has only linkedLineId: match if lineId matches (ignore creativeId)
   * - If Ad has both: match only if BOTH lineId and creativeId match
   *
   * When `requireKeyValuesSet` is true, only ads whose key-values were set
   * on the ad call for the current opportunity are considered (disambiguates
   * sibling campaigns sharing a creative ID).
   *
   * @param lineId Optional line ID to match
   * @param creativeId Optional creative ID to match
   * @param requireKeyValuesSet When true, restrict candidates to ads with non-empty keyValues
   * @returns True if any ad in this campaign matches the provided criteria
   */
  public isAssociatedWith(
    lineId?: string,
    creativeId?: string,
    requireKeyValuesSet = false,
  ): boolean {
    logger.debug(
      `Checking campaign association with line ID: ${lineId}, creative ID: ${creativeId}`,
    );
    return this.ads.some((ad) => {
      if (requireKeyValuesSet && !ad.hasKeyValues()) {
        return false;
      }

      if (lineId && creativeId) {
        return ad.linkedLineId === lineId && ad.linkedCreativeId === creativeId;
      }

      if (lineId && !creativeId) {
        return ad.linkedLineId === lineId;
      }

      if (!lineId && creativeId) {
        return ad.linkedCreativeId === creativeId;
      }

      return false;
    });
  }

  public enableBrandSafetyMonitoring(
    contentSelectors?: string,
    placementVisible?: () => boolean,
  ): void {
    if (!this.data.bkwds || this.brandSafety) {
      return;
    }

    if (placementVisible) {
      this.placementVisible = placementVisible;
    }

    try {
      this.brandSafety = this.brandSafetyManager.getBrandSafetyForCampaign(
        this.id,
        this.data.bkwds,
        contentSelectors,
      );

      if (this.brandSafety) {
        // Reactive channel for the post-render blur path: when a banned word becomes
        // visible (via debounced scroll/resize/mutation), BrandSafety dispatches here and
        // we forward to the placement. Key-value suppression itself is pulled lazily via
        // isBrandUnsafe(); these handlers keep the already-rendered-ad blur working.
        this.brandSafety.on('BrandSafetyComplianceViolation', (event) => {
          if (!this.activeBrandViolation) {
            logger.debug(
              `Brand safety violation detected for campaign ${this.id}: ${JSON.stringify(event.data.detectedWords)}`,
            );
            this.activeBrandViolation = true;
            this.dispatch('BrandSafetyComplianceViolation', event.data);
          }
        });

        this.brandSafety.on('BrandSafetyComplianceRestored', (event) => {
          if (this.activeBrandViolation) {
            logger.debug(`Brand safety compliance restored for campaign ${this.id}`);
            this.activeBrandViolation = false;
            this.dispatch('BrandSafetyComplianceRestored', event.data);
          }
        });
      }
    } catch (error) {
      logger.error(`Error enabling brand safety monitoring for campaign ${this.id}: ${error}`);
      this.brandSafety = null;
    }
  }

  public disableBrandSafetyMonitoring(): void {
    if (this.brandSafety) {
      this.brandSafety = null;
      logger.info(`Brand safety monitoring disabled for campaign ${this.id}`);
    }
  }

  public destroy(): void {
    this.disableBrandSafetyMonitoring();

    if (this.activityRecordedListener) {
      const activityManager = ActivityManager.getInstance();
      activityManager.off('ActivityRecorded', this.activityRecordedListener);
      this.activityRecordedListener = null;
    }
  }
}

export { Campaign };
